// Land layer (lane C, backlog C-3; territory-visuals.md §1): one cached bitmap per 16 × 16-cell
// chunk, redrawn only when the store marks it changed (its version, a player colour arriving, a
// new cache resolution); marching-squares contour fill (contour.ts), flat, no outline. Each
// bitmap carries a 1-cell pad drawn from the neighbours' cells, and is blitted with a 1 px
// overlap, so chunk seams never show.
//
// The 2.5D look is composed by the renderer from these bitmaps in two passes: the SIDE pass
// (every chunk at ground level, then darkened to V × sideShade with a source-atop black at
// 1 − sideShade) and the TOP pass (every chunk lifted ARENA_CLIENT.liftCells up the screen), so
// the side band only shows along bottom-facing edges. Player colours are our own palette
// (D-V2); the bitmaps hold main colours only.
//
// Cache: bitmaps at a resolution on a 2^(1/4) ladder of the screen's device px per cell (capped,
// so memory stays bounded), least-recently-used eviction past MAX_BITMAPS. Redraw ≤ 2 ms per
// dirty chunk.

import { ARENA_NET } from '@ballio/shared';
import { type Canvas2D, type Ctx2D, createCanvas, get2d, sizeCanvas } from '../render/canvas.js';
import { css } from '../render/theme.js';
import type { ArenaView } from './camera.js';
import { contourPolygons } from './contour.js';
import type { ArenaStore } from './store.js';

const CHUNK = ARENA_NET.chunkSize;
/** Cells of padding around each chunk bitmap. */
export const CHUNK_PAD = 1;
/** Bitmaps kept at most (≈ 3 screens of chunks). */
export const MAX_BITMAPS = 72;
/** Cache resolutions, device px per cell. */
export const MIN_CACHE_PX = 4;
export const MAX_CACHE_PX = 40;

/** The cache resolution for a screen resolution (device px per cell). */
export function cacheResolution(devicePxPerCell: number): number {
  if (!(devicePxPerCell > 0)) return MIN_CACHE_PX;
  const k = Math.ceil(Math.log2(devicePxPerCell) * 4 - 1e-9);
  const px = Math.pow(2, k / 4);
  return Math.min(MAX_CACHE_PX, Math.max(MIN_CACHE_PX, Math.ceil(px)));
}

interface ChunkBitmap {
  canvas: Canvas2D;
  ctx: Ctx2D;
  version: number;
  players: number;
  px: number;
  empty: boolean;
  used: number;
}

export class LandLayer {
  private readonly store: ArenaStore;
  private readonly cache = new Map<number, ChunkBitmap>();
  private epoch = -1;
  private useCounter = 0;
  /** Chunks redrawn by the last `prepare` (stats, tests). */
  redrawn = 0;

  constructor(store: ArenaStore) {
    this.store = store;
  }

  /** Chunk index range in view. */
  visibleChunks(view: ArenaView): { kx0: number; ky0: number; kx1: number; ky1: number } {
    const hw = view.width / 2 / view.scale + CHUNK_PAD;
    const hh = view.height / 2 / view.scale + CHUNK_PAD + 1;
    const s = this.store;
    return {
      kx0: Math.max(0, Math.floor((view.cx - hw) / CHUNK)),
      ky0: Math.max(0, Math.floor((view.cy - hh) / CHUNK)),
      kx1: Math.min(s.chunksX - 1, Math.floor((view.cx + hw) / CHUNK)),
      ky1: Math.min(s.chunksY - 1, Math.floor((view.cy + hh) / CHUNK)),
    };
  }

  /** Redraws the visible chunks that changed; returns how many were redrawn. */
  prepare(view: ArenaView, deviceScale: number): number {
    const s = this.store;
    if (s.landEpoch !== this.epoch) {
      this.epoch = s.landEpoch;
      for (const b of this.cache.values()) b.version = -1;
    }
    const px = cacheResolution(view.scale * deviceScale);
    const r = this.visibleChunks(view);
    this.redrawn = 0;
    for (let ky = r.ky0; ky <= r.ky1; ky++) {
      for (let kx = r.kx0; kx <= r.kx1; kx++) {
        const i = ky * s.chunksX + kx;
        const b = this.cache.get(i);
        const v = s.chunkVersion[i] as number;
        if (b && b.version === v && b.px === px && b.players === s.playersVersion) {
          b.used = ++this.useCounter;
          continue;
        }
        this.drawChunk(kx, ky, px, b);
        this.redrawn++;
      }
    }
    this.evict();
    return this.redrawn;
  }

  /**
   * Blits the visible chunk bitmaps into `ctx` (CSS px transform already set), shifted by
   * `offsetY` CSS px. Call `prepare` first in the frame.
   */
  blit(ctx: Ctx2D, view: ArenaView, offsetY: number): void {
    const s = this.store;
    const r = this.visibleChunks(view);
    const scale = view.scale;
    const ox = view.width / 2 - view.cx * scale;
    const oy = view.height / 2 - view.cy * scale + offsetY;
    for (let ky = r.ky0; ky <= r.ky1; ky++) {
      for (let kx = r.kx0; kx <= r.kx1; kx++) {
        const b = this.cache.get(ky * s.chunksX + kx);
        if (!b || b.empty) continue;
        const span = CHUNK + CHUNK_PAD * 2;
        const dx = ox + (kx * CHUNK - CHUNK_PAD) * scale;
        const dy = oy + (ky * CHUNK - CHUNK_PAD) * scale;
        ctx.drawImage(b.canvas as CanvasImageSource, dx, dy, span * scale, span * scale);
      }
    }
  }

  /** Forget every bitmap (context loss, a new room). */
  clear(): void {
    this.cache.clear();
  }

  private drawChunk(kx: number, ky: number, px: number, reuse: ChunkBitmap | undefined): void {
    const s = this.store;
    const i = ky * s.chunksX + kx;
    const span = CHUNK + CHUNK_PAD * 2;
    const size = span * px;
    let b = reuse;
    if (!b) {
      let canvas: Canvas2D;
      try {
        canvas = createCanvas(size, size);
      } catch {
        return;
      }
      const ctx = get2d(canvas);
      if (!ctx) return;
      b = { canvas, ctx, version: -1, players: -1, px, empty: true, used: 0 };
      this.cache.set(i, b);
    }
    sizeCanvas(b.canvas, size, size);
    const ctx = b.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const x0 = kx * CHUNK - CHUNK_PAD;
    const y0 = ky * CHUNK - CHUNK_PAD;
    // Owners in the sample window (one more cell each side: the squares need both corners).
    const ids = new Set<number>();
    for (let cy = y0 - 1; cy <= y0 + span; cy++) {
      for (let cx = x0 - 1; cx <= x0 + span; cx++) {
        const id = s.ownerAt(cx, cy);
        if (id !== 0) ids.add(id);
      }
    }
    b.empty = ids.size === 0;
    if (!b.empty) {
      ctx.setTransform(px, 0, 0, px, -x0 * px, -y0 * px);
      const at = (cx: number, cy: number): number => s.ownerAt(cx, cy);
      for (const id of ids) {
        const polys = contourPolygons(at, id, x0 - 1, y0 - 1, x0 + span, y0 + span);
        if (polys.length === 0) continue;
        ctx.fillStyle = css(s.colorOf(id));
        ctx.beginPath();
        for (const p of polys) {
          ctx.moveTo(p[0] as number, p[1] as number);
          for (let k = 2; k < p.length; k += 2) ctx.lineTo(p[k] as number, p[k + 1] as number);
          ctx.closePath();
        }
        ctx.fill();
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    b.version = s.chunkVersion[i] as number;
    b.players = s.playersVersion;
    b.px = px;
    b.used = ++this.useCounter;
  }

  private evict(): void {
    if (this.cache.size <= MAX_BITMAPS) return;
    const byAge = [...this.cache.entries()].sort((a, b) => a[1].used - b[1].used);
    for (let k = 0; k < byAge.length - MAX_BITMAPS; k++) {
      this.cache.delete((byAge[k] as [number, ChunkBitmap])[0]);
    }
  }
}
